// Shared shell chrome for every tracking tab: Add button, "Include archived"
// toggle, pagination controls with live-region announcement, and an
// emptyState slot. Keeps all 5 tabs visually consistent and reduces
// per-tab boilerplate. Children slot carries the TrackingTable (or whatever
// the consumer wants to render when items exist).

import { type ReactNode } from "react";
import { Plus, ChevronLeft, ChevronRight } from "lucide-react";
import Button from "../../../common/Button";
import {
  TRACKING_TYPE_LABELS,
  TRACKING_TYPE_LABELS_SINGULAR,
  type TabLevelType,
} from "../../../../types";

export interface TrackingPaginationState {
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  page: number;
  totalPages: number;
}

export interface TrackingTabShellProps {
  type: TabLevelType;
  onAdd: () => void;
  includeArchived: boolean;
  onToggleArchived: (value: boolean) => void;
  isEmpty: boolean;
  /** Cursor-pager state. Optional so tabs that own their own
   *  client-side pagination (e.g. DeliverableTab consumes the
   *  contract-wide hook directly and renders <Pagination> below
   *  the table) can omit it — the shell then skips the Prev/Next
   *  pair and the live region. */
  paginationState?: TrackingPaginationState;
  emptyState?: ReactNode;
  children: ReactNode;
  /** Button variant for the Add action. KPI tab's Overview toggle
   *  demotes Add to secondary so the bulk-log button can own the
   *  primary slot. Default primary preserves existing behaviour for
   *  every other tab. */
  addVariant?: "primary" | "secondary";
  /** Optional secondary action rendered next to the Add button.
   *  Used by KPI Overview to surface "Log period (bulk)" as the
   *  primary action. */
  secondaryAction?: ReactNode;
  /** Tabs that own their own Include-archived toggle (e.g.
   *  DeliverableTab uses the FilterChipBar chip) opt out so two
   *  controls don't both write to the same state. Defaults true
   *  for KPI / Insurance / Milestone tabs. */
  showIncludeArchived?: boolean;
  /** Override for the Add button label. Defaults to `Add {singular}`.
   *  KPI tab passes `New KPI` so the verb disambiguates from the
   *  sibling primary action `+ Log period (bulk)` (Add KPI defines a
   *  KPI; Log period records data against an existing one). */
  addLabel?: string;
}

export default function TrackingTabShell({
  type,
  onAdd,
  includeArchived,
  onToggleArchived,
  isEmpty,
  paginationState,
  emptyState,
  children,
  addVariant = "primary",
  secondaryAction,
  showIncludeArchived = true,
  addLabel,
}: TrackingTabShellProps) {
  const plural = TRACKING_TYPE_LABELS[type];
  const singular = TRACKING_TYPE_LABELS_SINGULAR[type];
  const buttonLabel = addLabel ?? `Add ${singular}`;

  const defaultEmpty = (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-12 text-center dark:border-slate-700 dark:bg-slate-800">
      <p className="text-body text-slate-500 dark:text-slate-400">
        No {plural.toLowerCase()} recorded yet.
      </p>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {showIncludeArchived && (
            <label className="inline-flex items-center gap-2 text-caption text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                className="accent-brand-600"
                checked={includeArchived}
                onChange={(e) => onToggleArchived(e.target.checked)}
              />
              Include archived
            </label>
          )}
        </div>
        <div className="flex items-center gap-3">
          {secondaryAction}
          <Button
            variant={addVariant}
            icon={<Plus className="h-4 w-4" />}
            onClick={onAdd}
          >
            {buttonLabel}
          </Button>
          {paginationState && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                iconOnly
                size="sm"
                icon={<ChevronLeft className="h-4 w-4" />}
                aria-label="Previous page"
                onClick={paginationState.onPrev}
                disabled={!paginationState.canPrev}
              />
              <Button
                variant="ghost"
                iconOnly
                size="sm"
                icon={<ChevronRight className="h-4 w-4" />}
                aria-label="Next page"
                onClick={paginationState.onNext}
                disabled={!paginationState.canNext}
              />
            </div>
          )}
        </div>
      </div>

      {paginationState && (
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          Page {paginationState.page} of {paginationState.totalPages}
        </span>
      )}

      {isEmpty ? emptyState ?? defaultEmpty : children}
    </div>
  );
}
